import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useConfig } from "../../config/ConfigContext";
import { useMeals } from "../Meal/MealsContext";
import { N8N_ORDER_CONFIRMATION_URL } from "../../constants";
import { serializeCartForOrder } from "../../utils/serializeCartForOrder";
import { computeOrderTotals } from "../../utils/orderPricing";
import { normalizeConfig } from "../../utils/normalizeConfig";
import CartSummary from "./CartSummary";
import OrderTotals from "./OrderTotals";
import OrderDateField from "./OrderDateField";
import SubmitOrCheckoutButton from "./SubmitOrCheckoutButton";
import CancellationMessage from "./CancellationMessage";

function Order() {
  const config = useConfig();
  const { cart, categorizedCart } = useMeals();
  const navigate = useNavigate();
  const noCheckout = config.noCheckout || config.id === "versionNoCheckout";
  const [orderDate, setOrderDate] = useState(() => new Date());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const totalItemPriceTTC = cart.reduce(
    (sum, item) =>
      sum + item.quantity * item.meal.fields[config.itemPriceFieldTTC],
    0
  );
  const totalItemPriceHT = cart.reduce(
    (sum, item) =>
      sum + item.quantity * item.meal.fields[config.itemPriceFieldHT],
    0
  );
  const {
    deliveryFee,
    totalDiscount,
    totalPriceDisplay: totalPrice,
    totalPriceHT,
    partnerCommissionPercent,
  } = computeOrderTotals(config, totalItemPriceTTC, totalItemPriceHT);
  const cfg = normalizeConfig(config);

  const handleSubmit = async (orderDateForCheckout) => {
    if (noCheckout) {
      setIsSubmitting(true);
      setSubmitError(null);
      const dateToUse = orderDateForCheckout ?? orderDate;
      const formData = {
        orderDate: dateToUse,
        deliveryDate: null,
        latestDeliveryTime: null,
        boatName: null,
        boatModel: null,
        recipientName: null,
        recipientPhone: null,
        firstName: null,
        lastName: null,
        email: null,
        numberOfPeople: null,
        googleMapsLink: null,
        deliveryNotes: null,
        dietarySpecifications: null,
        companyName: null,
        address1: null,
        postcode: null,
        city: null,
        country: null,
      };
      try {
        const orderResponse = await fetch(N8N_ORDER_CONFIRMATION_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            formData,
            cart: serializeCartForOrder(cart, config),
            config,
            totalPriceDisplay: totalPrice,
            totalPriceHT,
            totalDiscount,
            deliveryFee,
            totalItemPriceTTC,
            partnerCommissionPercent,
          }),
        });
        const orderResponseData = await orderResponse.json();
        if (orderResponseData.success && orderResponseData.orderNumber) {
          navigate("/confirmation", {
            state: {
              orderNumber: orderResponseData.orderNumber,
              firstName: "",
              lastName: "",
              noPayment: true,
            },
          });
        } else {
          setSubmitError("Order submission failed. Please try again.");
        }
      } catch (error) {
        console.error("Error submitting order:", error);
        setSubmitError("An error occurred. Please try again.");
      } finally {
        setIsSubmitting(false);
      }
      return;
    }
    navigate("/checkout");
  };

  return (
    <div
      id="order"
      className="bg-white p-8 mt-8 rounded-lg shadow-md w-full md:w-1/2 mx-auto text-center"
    >
      <h2 className="text-2xl font-semibold text-gray-800 mb-4 uppercase">
        My cart
      </h2>
      <CartSummary categorizedCart={categorizedCart} config={config} />

      {cart.length > 0 && (
        <OrderTotals
          deliveryFee={deliveryFee}
          totalDiscount={totalDiscount}
          totalPrice={totalPrice}
          deliveryFreeThreshold={config.deliveryFreeThreshold}
          showDeliveryFee={cfg.showDeliveryFeeInSummary}
          showPartnerDiscount={cfg.showPartnerDiscountInSummary}
        />
      )}

      {cart.length === 0 && (
        <p className="text-sm text-red-500">Please select at least 1 item.</p>
      )}

      {noCheckout && cart.length > 0 && (
        <OrderDateField value={orderDate} onChange={setOrderDate} />
      )}

      <SubmitOrCheckoutButton
        noCheckout={noCheckout}
        disabled={cart.length === 0}
        isSubmitting={isSubmitting}
        orderDate={orderDate}
        onSubmit={handleSubmit}
      />
      {noCheckout && submitError && (
        <p className="text-sm text-red-500 mt-2">{submitError}</p>
      )}

      <CancellationMessage />
    </div>
  );
}

export default Order;
